// GDBS Web — main application entry point
import { getRegisteredReport, generateReport, synthesizeReportFromActiveView } from './report-gen.js';
// The ONE client-side trust ladder (mirrors wasm/src/geonum.rs classify).
import { verdictFromDrift } from './gti-ladder.js';
import { preKiosk, initKioskMode } from './kiosk-mode.js';
import * as shareUI from './share-ui.js';
import * as landing from './landing.js';
import * as auth from './auth.js';
import * as queryEditor from './query-editor.js';
import * as dbBrowser from './db-browser.js';
import * as importWizard from './import-wizard.js';
import * as materialsTabs from './materials-tabs.js';
import * as geophysicsTabs from './geophysics-tabs.js';
import * as fluidsTabs from './fluids-tabs.js';
import * as quantumTabs from './quantum-tabs.js';
import * as admin from './admin.js';
import * as history from './history.js';
import * as dsoModule from './dso-module.js';
import { initTheoryGravity } from './theory-gravity.js';
import './geonum-trust.js'; // registers window.gdbsTrust for the global trust indicator
import { initAppGuide, applyAppGuideAccess } from './app-guide.js';
import { initComputeNode, applyComputeNode } from './client-worker.js';
import * as advModule from './adv-module.js';
import * as legalTabs from './legal-tabs.js';
import { usageLog } from './api.js';

// ────────────────────────────────────────────────────────────────────────────
// TIER-GATED LAZY CHUNKS
//
// PRO (plasma/molecular/cosmic) and HPC (hpc-lab + the hpc-* engine set,
// theory-lab, tukey, ns2d/heat/rayleigh/bssn/ligo/lbm/io) engine modules are
// NO LONGER static imports. They are loaded on demand via await import() from
// inside the post-auth gating callback, and ONLY when the signed-in user is
// entitled to that tier. esbuild (splitting:true) auto-emits each dynamic
// import target as its own lazy chunk; the core/auth/std graph no longer pulls
// the PRO/HPC engine code into the initial app bundle.
//
// HARD RULE: these loads run AFTER auth resolves and are each wrapped in
// try/catch, so a failed tier chunk can NEVER abort boot or sign-in. Core +
// auth + the free Standard engines work even if every tier chunk fails to load.
// Each loader is idempotent (guarded by a "loaded" flag) so re-entry on a
// second onAuthenticated fire (e.g. re-login) does not double-init.
// ────────────────────────────────────────────────────────────────────────────
let _proMods   = null;   // { plasmaTabs, molecularTabs, cosmicTabs }
let _hpcMods   = null;   // { hpcLab, tukeyTabs, lbm, hpcIoUtils, ... }
let _theoryMod = null;   // { theoryLab }

async function _loadProTier() {
  if (_proMods) return _proMods;
  // Each engine module loaded in parallel; init_ui() wires forms, gating runs
  // after so the paywall->content swap happens on this same authed pass.
  const [plasmaTabs, molecularTabs, cosmicTabs] = await Promise.all([
    import('./plasma-tabs.js'),
    import('./molecular-tabs.js'),
    import('./cosmic-tabs.js'),
  ]);
  plasmaTabs.init_ui();
  molecularTabs.init_ui();
  cosmicTabs.init_ui();
  // Warm the PRO engines' WASM off the boot critical path (idle), same rationale as
  // the STD engines: don't block time-to-interactive on the ~3.3MB engine binary.
  const _warmProEngines = () => {
    try { plasmaTabs.initWasm(); } catch (_) {}
    try { molecularTabs.initWasm(); } catch (_) {}
    try { cosmicTabs.initWasm(); } catch (_) {}
  };
  (window.requestIdleCallback
    ? window.requestIdleCallback(_warmProEngines, { timeout: 3000 })
    : setTimeout(_warmProEngines, 800));
  _proMods = { plasmaTabs, molecularTabs, cosmicTabs };
  return _proMods;
}

// Theory Lab is FREE for any active account (its own applyTheoryGating treats a
// valid license as access), so it loads separately from the gated HPC engine
// set — an active non-HPC user gets Theory without pulling the heavy hpc-* graph.
// Q-Desic is published external theory re-homed INTO Theory (not PRO), so it
// loads here too; the DSO de-pollution then relocates panels and is safe to run
// once Theory + Q-Desic init are done (cosmic DSO panels are static HTML, moved
// by id, so they do not require the PRO chunk).
async function _loadTheory() {
  if (_theoryMod) return _theoryMod;
  const [theoryLab, qdesic] = await Promise.all([
    import('./theory-lab.js'),
    import('./qdesic-cosmo.js'),
  ]);
  theoryLab.init_ui();
  try { qdesic.initQDesicCosmo(); } catch (_) {}
  _theoryMod = { theoryLab };
  _maybeRunDsoDepollution();
  return _theoryMod;
}

async function _loadHpcTier() {
  if (_hpcMods) return _hpcMods;
  const [
    hpcLab, tukeyTabs,
    ns2d, ns2dSpec, heat2d, radiation, rayleigh,
    bssn, ligo, lbm, hpcIo, hpcIoArxiv, hpcIoUtils,
  ] = await Promise.all([
    import('./hpc-lab.js'),
    import('./tukey-tabs.js'),
    import('./hpc-ns2d.js'),
    import('./hpc-ns2d-spectral.js'),
    import('./hpc-heat2d.js'),
    import('./hpc-radiation.js'),
    import('./hpc-rayleigh.js'),
    import('./hpc-bssn.js'),
    import('./hpc-ligo.js'),
    import('./gpu-lbm.js'),
    import('./hpc-io.js'),
    import('./hpc-io-arxiv.js'),
    import('./hpc-io-utils.js'),
  ]);

  // UI wiring (was top-level boot before the split).
  hpcLab.init_ui();
  tukeyTabs.init_ui();
  ns2d.initNS2D();
  ns2dSpec.initNS2DSpectral();
  heat2d.initHeat2D();
  radiation.initRadiation();
  rayleigh.initRayleigh();
  bssn.initBssn();
  ligo.init_ui();
  hpcIo.initHpcIO();
  hpcIoArxiv.initArxivSolver();
  hpcIoUtils.initConsole('io-console-input', 'io-console-output');
  hpcIoUtils.createNotebook('io-notebook-cells');
  hpcIoUtils.handleShareURL();

  _hpcMods = { hpcLab, tukeyTabs, lbm, hpcIoUtils };
  _wireHpcIoHandlers();   // history/diff/lbm DOM handlers (need hpcIoUtils + lbm)
  return _hpcMods;
}

// Entitlement predicates — mirror each module's own applyXGating() exactly.
function _isProEntitled(user, license) {
  const tier = license?.tier || 'trial';
  const mods = license?.modules || [];
  // plasma/medical/cosmic each unlock on full-access tier, their own module, or active trial.
  return auth.hasFullAccess(tier)
    || mods.includes('plasma') || mods.includes('medical') || mods.includes('cosmic')
    || (tier === 'trial' && license?.isActive)
    || user?.role === 'admin';
}
function _isHpcEntitled(user, license) {
  const tier = license?.tier || 'trial';
  const mods = license?.modules || [];
  // hpc-lab gate: beta|admin|enterprise | hpclab module | active trial.
  return ['beta', 'admin', 'enterprise'].includes(tier)
    || mods.includes('hpclab')
    || (tier === 'trial' && license?.isActive)
    || user?.role === 'admin';
}
import { getLicense } from './auth.js';
import { initCoraAgent, openAgentPanel, closeAgentPanel, sendAgentQuery, notifyComputed } from './cora-agent.js';
import { initSandbox, applySandboxAccess } from './sandbox.js';
import * as events from './events.js';

// Expose getLicense for cora-agent tier check, and agent handlers for HTML onclick
window._getLicense = getLicense;
window._coraAgent  = { open: openAgentPanel, close: closeAgentPanel, send: sendAgentQuery, _notify: notifyComputed };

const MODULE_VIEWS = ['plasma', 'molecular', 'cosmic', 'materials', 'topology', 'geophysics', 'fluids', 'quantum', 'legal', 'tukey', 'qdesic', 'hpcsim', 'sandbox'];
const EXTENSION_VIEWS = ['hpc', 'theory'];

// --- Tier Dot Selector ---
document.querySelectorAll('.tier-dot').forEach(dot => {
  dot.addEventListener('click', () => {
    const tier = dot.dataset.tier;

    document.querySelectorAll('.tier-dot').forEach(d => d.classList.remove('active'));
    document.querySelectorAll('.nav-tier-section').forEach(s => s.classList.remove('active'));

    dot.classList.add('active');
    document.querySelector(`.nav-tier-section[data-tier="${tier}"]`).classList.add('active');
  });
});

// Bridge gdbslog op-records -> the server usage log (usageLog.compute ->
// /api/log/batch), so the admin activity view captures EVERY engine run - not
// just the *-research.js engines that call usageLog.compute directly. gdbslog is
// otherwise a client-only audit log with no server forward. Those 5 modules
// self-log, so skip them here to avoid double-counting; everything else (plugins,
// the Engine Runner, hpc-* engines, worker dispatches) is forwarded once.
const _USAGE_SELF_LOGGED = new Set(['cosmic', 'geophysics', 'materials', 'medical', 'topology']);
function _wireUsageBridge() {
  if (!(typeof window !== 'undefined' && window.gdbslog && window.gdbslog.addSink)) { setTimeout(_wireUsageBridge, 500); return; }
  if (window.__usageBridge) return;
  window.__usageBridge = true;
  window.gdbslog.addSink((rec) => {
    if (!rec || rec.kind !== 'op' || !rec.op) return;
    if (_USAGE_SELF_LOGGED.has(rec.logger)) return;
    // plugin:* loggers are the plugin's own gdbs.log() host metadata (popover
    // only); the actual run is logged under the engine name by plugin-host.
    // Skipping them avoids 2-3 rows per run.
    if (typeof rec.logger === 'string' && rec.logger.indexOf('plugin:') === 0) return;
    try { usageLog.compute(rec.logger || 'engine', rec.op, rec.ms || 0, rec.fields || {}); } catch (e) {}
  });
}
_wireUsageBridge();

// Bridge gdbslog op-records -> the Sandbox run history. This widens run capture
// beyond the Physics Workbench (engine-runner): every engine/plugin/module that
// streams a structured op record through gdbslog is captured into the user's
// Sandbox history. Best-effort: sandbox.js's __sandboxCaptureFromLog no-ops when
// the user is not entitled, never throws, and never blocks the run. The
// engine-runner path uses its own __sandboxCaptureRun (with real params) and
// marks the op (__sandboxMarkDirectOp) so this bridge skips the duplicate op
// record for that run.
function _wireSandboxCapture() {
  if (!(typeof window !== 'undefined' && window.gdbslog && window.gdbslog.addSink)) { setTimeout(_wireSandboxCapture, 500); return; }
  if (window.__sandboxCaptureBridge) return;
  window.__sandboxCaptureBridge = true;
  window.gdbslog.addSink((rec) => {
    if (!rec || rec.kind !== 'op' || !rec.op) return;
    try { window.__sandboxCaptureFromLog && window.__sandboxCaptureFromLog(rec); } catch (e) {}
  });
}
_wireSandboxCapture();

// --- System notice banner (UI-refresh announcement) ---
(function initSystemNotice() {
  const NOTICE_KEY = 'gdbs_notice_dismissed_v2';   // bump suffix for new announcements
  const notice = document.getElementById('system-notice');
  if (!notice) return;
  if (localStorage.getItem(NOTICE_KEY)) {
    notice.classList.add('hidden');
    return;
  }
  document.getElementById('system-notice-close')?.addEventListener('click', () => {
    notice.classList.add('hidden');
    try { localStorage.setItem(NOTICE_KEY, '1'); } catch {}
  });
})();

// --- View navigation ---
// Activate a view by name, syncing nav-item highlight and usage logging.
// Exposed as window.gdbsShowView so non-sidebar entry points (e.g. the user
// menu items for API / GQL) can switch views without a sidebar button.
function activateView(view, srcBtn) {
  const target = document.getElementById('view-' + view);
  if (!target) return;
  document.querySelectorAll('.nav-item').forEach(b => b.classList.remove('active'));
  document.querySelectorAll('.view').forEach(v => v.classList.remove('active'));
  if (srcBtn) srcBtn.classList.add('active');
  target.classList.add('active');

  if (MODULE_VIEWS.includes(view)) {
    usageLog.module(view);
  } else if (EXTENSION_VIEWS.includes(view)) {
    usageLog.extension(view);
  }
}
window.gdbsShowView = activateView;

// Hash-based deep-link routing. URLs like `#hpclab/helicity` or `#hpclab/phasefield`
// activate the named view and then click the matching .hpc-tab[data-ht] inside it.
// Lets us send Penna / Boris / investors a direct link to a specific benchmark
// instead of a navigate-three-times instruction.
function applyHashRoute() {
  const h = (location.hash || '').replace(/^#/, '').trim();
  if (!h) return;
  const [view, tab] = h.split('/');
  if (view) {
    const navBtn = document.querySelector(`.nav-item[data-view="${view}"]`);
    try { activateView(view, navBtn || null); } catch (_) {}
  }
  if (tab) {
    // Defer so the view's panels are in the DOM before we trigger the tab.
    setTimeout(() => {
      const tbtn = document.querySelector(`.hpc-tab[data-ht="${tab}"]`);
      if (tbtn) tbtn.click();
    }, 150);
  }
}
window.addEventListener('hashchange', applyHashRoute);
// Fire once at load (after activateView default has run).
setTimeout(applyHashRoute, 200);

document.querySelectorAll('.nav-item').forEach(btn => {
  btn.addEventListener('click', () => activateView(btn.dataset.view, btn));
});

// Module sub-tabs collapse to icon-only on small screens (see app.css). Mirror
// each tab's text label into its title attribute so the label is still
// available on hover / long-press once the text is visually hidden.
(function setTabTitles() {
  document.querySelectorAll(
    '.mtab, .ctab, .mattab, .geotab, .fltab, .qtab, .tukeytab, .theory-tab'
  ).forEach(b => {
    if (b.title) return;
    const label = b.textContent.trim();
    if (label) b.title = label;
  });
})();

// --- Inject "?" doc reference badges on nav items that have a docs topic ---
// Maps each nav data-view to its /docs/<topic>.md guide. View names that don't
// match the doc id (molecular → medical) are remapped explicitly.
const NAV_DOC_REF = {
  materials:  'materials',
  topology:   'topology',
  geophysics: 'geophysics',
  fluids:     'fluids',
  quantum:    'quantum',
  plasma:     'plasma',
  molecular:  'medical',
  cosmic:     'cosmic',
  theory:     'theory',
};
document.querySelectorAll('.nav-item[data-view]').forEach(btn => {
  const topicId = NAV_DOC_REF[btn.dataset.view];
  if (!topicId) return;
  if (btn.querySelector('.nav-doc-ref')) return;
  const ref = document.createElement('span');
  ref.className = 'nav-doc-ref';
  ref.textContent = '?';
  ref.setAttribute('role', 'button');
  ref.setAttribute('aria-label', `Open ${btn.dataset.view} guide`);
  ref.title = `Open ${btn.dataset.view} guide`;
  ref.addEventListener('click', (e) => {
    e.stopPropagation();
    window.gdbsOpenDocs?.(topicId);
  });
  btn.appendChild(ref);
});

// --- Mobile fly-up navigation sheet ---
(function initFlyupNav() {
  const sidebar = document.querySelector('.sidebar');
  const toggle = document.getElementById('mobile-nav-toggle');
  const backdrop = document.getElementById('mobile-nav-backdrop');
  if (!sidebar || !toggle || !backdrop) return;
  const open = () => { sidebar.classList.add('flyup-open'); backdrop.classList.add('show'); };
  const close = () => { sidebar.classList.remove('flyup-open'); backdrop.classList.remove('show'); };
  toggle.addEventListener('click', () => sidebar.classList.contains('flyup-open') ? close() : open());
  backdrop.addEventListener('click', close);
  // choosing a module (or tier) closes the sheet; tier-dots keep it open to switch
  sidebar.querySelectorAll('.nav-item[data-view]').forEach(b => b.addEventListener('click', close));
})();

// --- Logout ---
document.getElementById('btn-logout').addEventListener('click', () => auth.logout());

// --- User menu dropdown (Report / Print / Theme / Help / Sign Out) ---
(function initUserMenu() {
  const trigger = document.getElementById('btn-user-menu');
  const menu = document.getElementById('user-menu');
  if (!trigger || !menu) return;

  const open = () => {
    menu.classList.remove('hidden');
    trigger.setAttribute('aria-expanded', 'true');
  };
  const close = () => {
    menu.classList.add('hidden');
    trigger.setAttribute('aria-expanded', 'false');
  };
  const toggle = () => menu.classList.contains('hidden') ? open() : close();

  trigger.addEventListener('click', (e) => {
    e.stopPropagation();
    toggle();
  });
  // Any item click closes the menu (the item's own onclick still runs).
  menu.querySelectorAll('.user-menu-item').forEach(item => {
    item.addEventListener('click', () => setTimeout(close, 0));
  });
  document.addEventListener('click', (e) => {
    if (!menu.classList.contains('hidden') &&
        !menu.contains(e.target) &&
        !trigger.contains(e.target)) {
      close();
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !menu.classList.contains('hidden')) close();
  });
})();

// --- Quick Report from active page ---
// Bypasses the reference-model modal — Report click pipes the active run
// (registered config if any, otherwise synthesized from the active view)
// straight into generateReport with no user input required. Same PDF
// pipeline, same citation, same divergence analysis when a registered
// config supplies its own reference, just no manual reference entry step.
window.gdbsQuickReport = async function gdbsQuickReport() {
  try {
    const cfg = getRegisteredReport() ?? await synthesizeReportFromActiveView();
    if (!cfg) {
      window.gdbsToastWarn?.('No active view to report on.', { timeoutMs: 3500 });
      return;
    }
    const empty = (cfg.series?.length ?? 0) === 0
               && (cfg.params?.length ?? 0) === 0
               && (cfg.images?.length ?? 0) === 0;
    if (empty) {
      const msg = 'Nothing to report on this page yet - run a computation first.';
      if (window.gdbsToastWarn) window.gdbsToastWarn(msg, { timeoutMs: 4000 });
      else alert(msg);
      return;
    }
    // Use the registered config's own reference if present; otherwise the
    // module's default citation string from _MODULE_REF_DEFAULTS (already
    // attached to the synthesized cfg as cfg.refDefault).
    const ref = cfg.refInline || cfg.refDefault || null;
    generateReport(cfg, ref);
  } catch (err) {
    console.error('[Report] generation failed:', err);
    window.gdbsToastError?.('Report generation failed: ' + (err?.message || err), { timeoutMs: 6000 });
  }
};

// The reference-model modal stays available for users who want to
// override the citation. Its generate button keeps the same behavior.
document.getElementById('btn-report-generate').addEventListener('click', async () => {
  try {
    const cfg = getRegisteredReport() ?? await synthesizeReportFromActiveView();
    if (!cfg || ((cfg.series?.length ?? 0) === 0 && (cfg.params?.length ?? 0) === 0
                 && (cfg.images?.length ?? 0) === 0)) {
      window.gdbsToastWarn?.('Nothing to report on this page yet.', { timeoutMs: 4000 });
      return;
    }
    const ref = document.getElementById('report-ref-input')?.value.trim() || cfg.refDefault || null;
    document.getElementById('report-modal').classList.add('hidden');
    generateReport(cfg, ref);
  } catch (err) {
    console.error('[Report] generation failed:', err);
    window.gdbsToastError?.('Report generation failed: ' + (err?.message || err), { timeoutMs: 6000 });
  }
});

// --- Universal Save Run ---
const TABLE_MODULE_MAP = {
  circular: 'plasma', shaped: 'plasma', negtri: 'plasma', stell: 'plasma',
  frc: 'plasma', eigen: 'plasma', sim: 'plasma', optimizer: 'plasma',
  binding: 'medical', stability: 'medical', nanoparticle: 'medical',
  interactions: 'medical', qsar: 'medical',
  rotation: 'cosmic', cmb: 'cosmic', blackhole: 'cosmic',
  constants: 'cosmic', ratios: 'cosmic',
  bandgap: 'materials', elastic: 'materials', phase: 'materials',
  thermal: 'materials', defect: 'materials',
  seismic: 'geophysics', stress: 'geophysics', heatflow: 'geophysics',
  gravity: 'geophysics', earthquake: 'geophysics',
  boundary: 'fluids', pipe: 'fluids', drag: 'fluids',
  shock: 'fluids', convection: 'fluids',
  qubit: 'quantum', decoherence: 'quantum', entanglement: 'quantum',
  qec: 'quantum', circuit: 'quantum',
  transport: 'hpclab', phasediag: 'hpclab', fem: 'hpclab',
  md: 'hpclab', batch: 'hpclab',
};

import { api } from './api.js';

window.saveRun = async function(scanType) {
  const table = document.getElementById('t-' + scanType);
  if (!table) return;
  const headers = Array.from(table.querySelectorAll('thead th')).map(th => th.textContent.trim());
  const rows = Array.from(table.querySelectorAll('tbody tr')).map(tr =>
    Array.from(tr.querySelectorAll('td')).map(td => td.textContent.trim())
  );
  if (!rows.length) { alert('No results to save. Run a scan first.'); return; }
  const module = TABLE_MODULE_MAP[scanType] || 'unknown';
  const btn = document.querySelector(`[data-save="${scanType}"]`);
  if (btn) { btn.disabled = true; btn.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Saving'; }
  try {
    const res = await api.saveRun(module, scanType, `${module}/${scanType}`, headers, rows);
    if (res.success) {
      if (btn) { btn.innerHTML = '<i class="fas fa-check"></i> Saved'; setTimeout(() => { btn.innerHTML = '<i class="fas fa-save"></i> Save'; btn.disabled = false; }, 2000); }
    } else {
      alert(res.error || 'Failed to save');
      if (btn) { btn.innerHTML = '<i class="fas fa-save"></i> Save'; btn.disabled = false; }
    }
  } catch (e) {
    alert('Save failed');
    if (btn) { btn.innerHTML = '<i class="fas fa-save"></i> Save'; btn.disabled = false; }
  }
};

// Ping CORA on load — shows Agent button only if CORA is reachable
initCoraAgent();

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.plasma-table').forEach(table => {
    const id = table.id?.replace('t-', '');
    if (!id || !TABLE_MODULE_MAP[id]) return;
    const fieldset = table.previousElementSibling;
    if (!fieldset) return;
    const runBtn = fieldset.querySelector('.btn-primary');
    if (!runBtn) return;
    const saveBtn = document.createElement('button');
    saveBtn.type = 'button';
    saveBtn.className = 'btn btn-sm btn-save';
    saveBtn.dataset.save = id;
    saveBtn.innerHTML = '<i class="fas fa-save"></i> Save';
    saveBtn.onclick = () => window.saveRun(id);
    runBtn.parentNode.insertBefore(saveBtn, runBtn.nextSibling);
  });
  // Also handle plasma-actions (plasma/molecular/cosmic have different structure)
  document.querySelectorAll('.plasma-actions').forEach(actions => {
    const form = actions.closest('form');
    if (!form) return;
    const formId = form.id?.replace('f-', '');
    if (!formId || !TABLE_MODULE_MAP[formId]) return;
    if (actions.querySelector('.btn-save')) return;
    const saveBtn = document.createElement('button');
    saveBtn.type = 'button';
    saveBtn.className = 'btn btn-save';
    saveBtn.dataset.save = formId;
    saveBtn.innerHTML = '<i class="fas fa-save"></i> Save';
    saveBtn.onclick = () => window.saveRun(formId);
    actions.appendChild(saveBtn);
  });
});

// --- Init modules ---
// Shared-preview (kiosk) links: suppress the landing flash before the async
// resolve runs; tryAutoLogin is skipped for ?share so the visitor stays anonymous.
preKiosk();
landing.init();
auth.init();
queryEditor.init();
dbBrowser.init();
importWizard.init();
materialsTabs.init_ui();
geophysicsTabs.init_ui();
fluidsTabs.init_ui();
quantumTabs.init_ui();
admin.init();
history.init();
initAppGuide();
initComputeNode();
advModule.init();
legalTabs.init_ui();
initSandbox();

// NOTE: PRO (plasma/molecular/cosmic/qdesic) and HPC (hpc-lab, theory-lab,
// tukey, ns2d/heat/rayleigh/bssn/ligo/lbm/io) engine init has MOVED into the
// gated lazy loaders (_loadProTier / _loadTheory / _loadHpcTier), invoked from
// the post-auth callback only when the user is entitled. Core/auth/STD init
// stays here so the shell + free Standard engines boot unconditionally.

// DSO de-pollution + Q-Desic re-home + genuine gravitation tools. These
// restructure / wire the Cosmos + Theory panels and so must run AFTER the PRO
// (cosmic + qdesic) and Theory chunks have loaded their init_ui (preserving the
// original "init_ui first, then relocate" ordering). _maybeRunDsoDepollution is
// idempotent and only fires once both chunks are present.
let _dsoDepollutionDone = false;
function _maybeRunDsoDepollution() {
  if (_dsoDepollutionDone) return;
  if (!_theoryMod) return;   // need Theory + Q-Desic panels wired
  _dsoDepollutionDone = true;
  try { dsoModule.buildDsoModule(); } catch (_) {}
  try { dsoModule.migrateGenuineToTheory(); } catch (_) {}
  try { initTheoryGravity(); } catch (_) {}
}

// Brand home — clicking GDBSPro in the header navigates to the user's home view
// (first non-hidden nav-item). Admins land on Query; everyone else on the first
// visible module after Query/Browser are admin-gated.
const brandHome = document.getElementById('brand-home');
if (brandHome) {
  const goHome = () => admin.navigateHome();
  brandHome.addEventListener('click', goHome);
  brandHome.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); goHome(); } });
}

// HPC Sim tab switching (view-hpcsim uses same .hpc-tab/.hpc-panel pattern as HPC Lab)
document.querySelectorAll('#view-hpcsim .hpc-tab').forEach(btn => {
  btn.addEventListener('click', () => {
    document.querySelectorAll('#view-hpcsim .hpc-tab').forEach(b => b.classList.remove('active'));
    document.querySelectorAll('#hpcsim-content .hpc-panel').forEach(p => p.classList.remove('active'));
    btn.classList.add('active');
    const panel = document.getElementById('hp-' + btn.dataset.ht);
    if (panel) panel.classList.add('active');
  });
});

// Shared-preview (kiosk) mode: if ?share={token}, resolve it and render only that
// one target read-only. Runs AFTER all module init so views/forms/plugins exist.
initKioskMode();

// HPC-IO History/Diff + LBM Sim DOM handlers. These reference the HPC-tier
// imports (hpc-io-utils, gpu-lbm), so they are wired only after the HPC chunk
// loads (called from _loadHpcTier). Guarded so a re-entry is a no-op.
function _wireHpcIoHandlers() {
  if (_wireHpcIoHandlers._done) return;
  _wireHpcIoHandlers._done = true;
  const { hpcIoUtils, lbm } = _hpcMods || {};
  if (!hpcIoUtils) return;
  const { getRunHistory, clearRunHistory, exportRunHistory, renderDiff } = hpcIoUtils;

  // HPC-IO History panel
  async function refreshHistory() {
    const filter = document.getElementById('io-hist-filter')?.value || '';
    const runs = await getRunHistory(filter || null, 200);
    const countEl = document.getElementById('io-hist-count');
    if (countEl) countEl.textContent = runs.length + ' runs';
    const tbl = document.getElementById('io-hist-table');
    if (!tbl) return;
    if (!runs.length) { tbl.innerHTML = '<p style="color:#475569;font-size:12px;text-align:center;margin:40px 0;">No runs recorded yet. Computations are saved automatically.</p>'; return; }
    tbl.innerHTML = '<table style="width:100%;border-collapse:collapse;font-size:11px;"><thead><tr><th style="padding:4px 8px;color:#64748b;text-align:left;">Date</th><th style="padding:4px 8px;color:#64748b;">Module</th><th style="padding:4px 8px;color:#64748b;">Type</th><th style="padding:4px 8px;color:#64748b;">Status</th></tr></thead><tbody>' +
      runs.map(r => `<tr><td style="padding:3px 8px;border:1px solid #1e293b;color:#94a3b8;">${new Date(r.timestamp).toLocaleString()}</td><td style="padding:3px 8px;border:1px solid #1e293b;">${r.module}</td><td style="padding:3px 8px;border:1px solid #1e293b;">${r.scanType}</td><td style="padding:3px 8px;border:1px solid #1e293b;">${r.result?.status || ''}</td></tr>`).join('') +
      '</tbody></table>';
  }
  document.getElementById('io-hist-refresh')?.addEventListener('click', refreshHistory);
  document.getElementById('io-hist-filter')?.addEventListener('change', refreshHistory);
  document.getElementById('io-hist-clear')?.addEventListener('click', async () => { if (confirm('Clear all run history?')) { await clearRunHistory(); refreshHistory(); } });
  document.getElementById('io-hist-export')?.addEventListener('click', async () => {
    const csv = await exportRunHistory('csv');
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' })); a.download = 'gdbs-run-history.csv'; a.click();
  });

  // HPC-IO Diff panel
  document.getElementById('io-diff-compare')?.addEventListener('click', async () => {
    const runs = await getRunHistory(null, 200);
    if (runs.length < 2) { document.getElementById('io-diff-output').innerHTML = '<p style="color:#475569;">Need at least 2 runs.</p>'; return; }
    renderDiff('io-diff-output', runs[0], runs[1]);
  });

  // LBM Sim panel — wire to gpu-lbm.js engine
  _initLbmSim(lbm?.runLbmGPU);
}

// HPC-IO Flux demo switcher (event delegation)
document.getElementById('flux-demo-tabs')?.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-flux]');
  if (!btn) return;
  const tabs = document.getElementById('flux-demo-tabs');
  tabs.querySelectorAll('button').forEach(b => { b.style.background = 'transparent'; b.style.color = '#64748b'; b.style.borderBottom = '2px solid transparent'; });
  btn.style.background = 'rgba(139,92,246,0.12)'; btn.style.color = '#a78bfa'; btn.style.borderBottom = '2px solid #a78bfa';
  const frame = document.getElementById('flux-demo-frame');
  if (!frame) return;
  const map = { core: '/css/flux/flux-demo.html', components: '/css/flux/flux-components-demo.html', tables: '/css/flux/flux-table-demo.html' };
  frame.src = map[btn.dataset.flux] || map.core;
});

// HPC-IO tab switching
document.querySelectorAll('#view-hpcio .hpc-tab').forEach(btn => {
  btn.addEventListener('click', () => {
    document.querySelectorAll('#view-hpcio .hpc-tab').forEach(b => b.classList.remove('active'));
    document.querySelectorAll('#hpcio-content .hpc-panel').forEach(p => p.classList.remove('active'));
    btn.classList.add('active');
    const panel = document.getElementById('hp-' + btn.dataset.ht);
    if (panel) panel.classList.add('active');
  });
});

// Sync ns2d grid select to hidden ny input
document.getElementById('ns2d-nx')?.addEventListener('change', function() {
  const ny = document.getElementById('ns2d-ny');
  if (ny) ny.value = this.value;
});
// Sync heat2d grid select to hidden ny input
document.getElementById('heat2d-nx')?.addEventListener('change', function() {
  const ny = document.getElementById('heat2d-ny');
  if (ny) ny.value = this.value;
});

// ── LBM Sim panel — wire to gpu-lbm.js engine ──
// Called from _wireHpcIoHandlers (HPC chunk) with the lazily-loaded engine fn.
function _initLbmSim(runLbmGPU) {
  const form = document.getElementById('f-lbm-sim');
  if (!form || typeof runLbmGPU !== 'function') return;
  const lbmCharts = {};
  document.querySelectorAll('.lbm-sim-preset').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.lbm-sim-preset').forEach(b => {
        b.classList.remove('active'); b.style.background = 'transparent';
        b.style.color = '#94a3b8'; b.style.borderColor = 'rgba(59,130,246,0.2)';
      });
      btn.classList.add('active'); btn.style.background = 'rgba(59,130,246,0.15)';
      btn.style.color = '#60a5fa'; btn.style.borderColor = 'rgba(59,130,246,0.4)';
      document.getElementById('lbm-sim-preset').value = btn.dataset.preset;
    });
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const btn = form.querySelector('button[type="submit"]');
    if (btn) { btn.disabled = true; btn.innerHTML = '<i class="fas fa-spinner fa-spin"></i> GPU LBM...'; }
    const st = document.getElementById('lbm-sim-status');
    if (st) { st.style.color = '#64748b'; st.textContent = 'Initializing LBM D2Q9 on WebGPU...'; }
    const t0 = performance.now();
    try {
      const nx = parseInt(document.getElementById('lbm-sim-nx').value) || 128;
      const re = parseFloat(document.getElementById('lbm-sim-re').value) || 100;
      const steps = parseInt(document.getElementById('lbm-sim-steps').value) || 5000;
      const benchmark = document.getElementById('lbm-sim-preset').value || 'lid_cavity';
      const tau = parseFloat(document.getElementById('lbm-sim-tau').value) || 0.8;
      const umax = parseFloat(document.getElementById('lbm-sim-umax').value) || 0.04;
      const reVal = parseFloat(document.getElementById('lbm-sim-re').value) || 0;
      const cfg = { name: `${benchmark} ${nx}x${nx}`, benchmark, nx, ny: nx, steps, tau, u_max: umax, snapshot_interval: Math.max(100, Math.floor(steps / 20)) };
      if (reVal > 0) cfg.reynolds = reVal;
      const r = await runLbmGPU(cfg);
      const ms = (performance.now() - t0).toFixed(0);
      if (st) st.textContent = `GPU LBM done in ${ms} ms | ${r.nx}x${r.ny} | Re=${r.reynolds.toFixed(0)} | ${r.total_steps} steps | KE=${r.final_kinetic_energy.toExponential(2)} | ${r.converged ? 'Converged' : 'Running'}`;

      const cv = document.getElementById('lbm-sim-vel-canvas');
      if (cv && r.snapshots.length) {
        const last = r.snapshots[r.snapshots.length - 1];
        const ctx = cv.getContext('2d');
        const w = cv.width = cv.clientWidth || 400, h = cv.height = cv.clientHeight || 280;
        const fnx = last.field_nx, fny = last.field_ny, field = last.velocity_field;
        const cw = w / fnx, ch = h / fny;
        let mx = 0;
        for (let k = 0; k < fnx * fny; k++) { const m = Math.sqrt(field[k * 2] ** 2 + field[k * 2 + 1] ** 2); if (m > mx) mx = m; }
        mx = mx || 1;
        for (let j = 0; j < fny; j++) for (let i = 0; i < fnx; i++) {
          const k = j * fnx + i;
          const t = Math.sqrt(field[k * 2] ** 2 + field[k * 2 + 1] ** 2) / mx;
          const rr = Math.round(255 * Math.max(0, Math.min(1, -0.53 + t * (3.13 * t - 1.32))));
          const gg = Math.round(255 * Math.max(0, Math.min(1, 0.005 + t * (0.87 - 0.28 * t))));
          const bb = Math.round(255 * Math.max(0, Math.min(1, 0.33 + t * (0.77 * t * t - 1.36 * t + 0.69))));
          ctx.fillStyle = `rgb(${rr},${gg},${bb})`;
          ctx.fillRect(i * cw, (fny - 1 - j) * ch, cw + 1, ch + 1);
        }
      }

      const makeChart = (id, cfg) => { if (lbmCharts[id]) { lbmCharts[id].destroy(); } const el = document.getElementById(id); if (!el) return; lbmCharts[id] = new Chart(el.getContext('2d'), cfg); };
      makeChart('lbm-sim-chart-ke', { type: 'scatter', data: { datasets: [{ label: 'KE', data: r.snapshots.map(s => ({ x: s.step, y: s.kinetic_energy })), borderColor: '#4fc3f7', borderWidth: 2, pointRadius: 1, showLine: true, fill: false }] },
        options: { responsive: true, maintainAspectRatio: false, plugins: { title: { display: true, text: 'Kinetic Energy', color: '#ccc' }, legend: { display: false } },
          scales: { x: { ticks: { color: '#888' } }, y: { ticks: { color: '#888' } } } } });

      const errData = r.snapshots.filter(s => s.l2_error > 0).map(s => ({ x: s.step, y: s.l2_error }));
      if (errData.length) {
        makeChart('lbm-sim-chart-err', { type: 'scatter', data: { datasets: [{ label: 'L2 error', data: errData, borderColor: '#10b981', borderWidth: 2, pointRadius: 1, showLine: true, fill: false }] },
          options: { responsive: true, maintainAspectRatio: false, plugins: { title: { display: true, text: 'L2 Error vs Analytical', color: '#ccc' }, legend: { display: false } },
            scales: { x: { ticks: { color: '#888' } }, y: { ticks: { color: '#888' }, type: 'logarithmic' } } } });
      }

      const dEl = document.getElementById('lbm-sim-drift');
      if (dEl && r.geonum) {
        const g = r.geonum;
        // `(x || 0).toFixed(3)` printed "0.000 shades" for a MISSING drift - the
        // same absence-becomes-best-figure default fixed at js/hpc-dft.js:272.
        // A missing figure now says so instead of impersonating a measured zero.
        const sh = d => (typeof d === 'number' && isFinite(d)) ? d.toFixed(3) : 'not reported';
        dEl.innerHTML = `<span style="color:#4fc3f7">GeoNum:</span> KE drift ${sh(g.kinetic_energy?.drift)} shades`
          + ` | Re drift ${sh(g.reynolds?.drift)}`
          + ` | Max drift ${sh(g.max_drift)}`
          // Shared ladder rather than a fourth inline copy; an unreported drift now
          // says so instead of silently reading DEGRADED.
          + ` | GTI mantissa: ${verdictFromDrift(g.max_drift) || 'not reported'}`;
      }
    } catch (err) {
      if (st) { st.textContent = 'Error: ' + err.message; st.style.color = '#f87171'; }
      console.error('LBM Sim error:', err);
    } finally {
      if (btn) { btn.disabled = false; btn.innerHTML = '<i class="fas fa-play"></i> Run LBM'; }
    }
  });
}

// --- GQL Reference: search & section toggle ---
window.toggleGqlSection = function(h3) {
  const toggle = h3.querySelector('.gql-toggle');
  const body = h3.nextElementSibling;
  if (body && body.classList.contains('gql-section-body')) {
    const collapsed = !body.classList.contains('collapsed');
    body.classList.toggle('collapsed', collapsed);
    if (toggle) toggle.classList.toggle('collapsed', collapsed);
  }
};

(function initGqlSearch() {
  const input = document.getElementById('gql-search');
  if (!input) return;
  input.addEventListener('input', () => {
    const q = input.value.trim().toLowerCase();
    const sections = document.querySelectorAll('#gql-body .gql-section');
    const noResults = document.getElementById('gql-no-results');
    let anyVisible = false;
    sections.forEach(section => {
      if (!q) {
        section.classList.remove('gql-hidden');
        const body = section.querySelector('.gql-section-body');
        if (body) body.classList.remove('collapsed');
        const toggle = section.querySelector('.gql-toggle');
        if (toggle) toggle.classList.remove('collapsed');
        anyVisible = true;
        return;
      }
      const text = section.textContent.toLowerCase();
      const match = text.includes(q);
      section.classList.toggle('gql-hidden', !match);
      if (match) {
        anyVisible = true;
        const body = section.querySelector('.gql-section-body');
        if (body) body.classList.remove('collapsed');
        const toggle = section.querySelector('.gql-toggle');
        if (toggle) toggle.classList.remove('collapsed');
      }
    });
    if (noResults) noResults.style.display = anyVisible ? 'none' : 'block';
  });
})();

// --- API Docs gating ---
function applyApiDocsGating(user, license) {
  const tier = license?.tier || 'trial';
  const modules = license?.modules || [];
  const isAdmin = user?.role === 'admin';
  const isTrial = tier === 'trial' && license?.isActive;

  const token = localStorage.getItem('gdbs_token') || license?.key || '';
  if (token) {
    const keyEls = ['api-key-display', 'api-key-inline', 'api-key-curl'];
    keyEls.forEach(id => { const el = document.getElementById(id); if (el) el.textContent = token; });
  }
  const baseUrl = window.location.origin;
  document.querySelectorAll('.api-url, .api-url-inline').forEach(el => el.textContent = baseUrl);
  document.getElementById('api-base-url')?.setAttribute('data-url', baseUrl);

  document.querySelectorAll('.api-module-section').forEach(section => {
    const mod = section.dataset.apiModule;
    if (mod === 'admin') {
      section.style.display = isAdmin ? '' : 'none';
    } else {
      const hasAccess = auth.hasFullAccess(tier) || modules.includes(mod) || isTrial;
      section.style.display = hasAccess ? '' : 'none';
    }
  });

  // API-key management panel (rotate-on-demand). Keys are a paid-tier feature.
  _apiDocsTier = tier;
  loadApiKeysList();
}

window.copyApiKey = function() {
  const key = document.getElementById('api-key-display')?.textContent;
  if (key && key !== 'Loading...') navigator.clipboard.writeText(key).then(() => alert('API key copied'));
};

// --- API key rotation (self-service) ---
let _apiDocsTier = 'trial';

function fmtDate(s) { if (!s) return '-'; const d = new Date(s); return isNaN(d) ? '-' : d.toLocaleDateString(); }

async function loadApiKeysList() {
  const listEl = document.getElementById('apikeys-list');
  const btn = document.getElementById('btn-rotate-apikey');
  if (!listEl) return;

  if (_apiDocsTier === 'trial') {
    listEl.innerHTML = '<span class="hint">API keys are available on paid plans. Your current plan is trial.</span>';
    if (btn) btn.style.display = 'none';
    return;
  }
  if (btn) btn.style.display = '';

  try {
    const res = await api.advListApiKeys();
    const keys = Array.isArray(res) ? res.filter(k => k.isActive) : [];
    if (!keys.length) {
      listEl.innerHTML = '<span class="hint">No active API key yet. Click "Rotate API key" to create one.</span>';
      return;
    }
    listEl.innerHTML = keys.map(k =>
      `<div class="apikey-row">`
      + `<code>${(k.keyPreview || '').replace(/[<>&]/g, '')}</code>`
      + `<span class="apikey-meta">${(k.name || 'API Key').replace(/[<>&]/g, '')} · created ${fmtDate(k.createdAt)}`
      + `${k.lastUsedAt ? ' · last used ' + fmtDate(k.lastUsedAt) : ' · never used'}</span>`
      + `</div>`).join('');
  } catch (_) {
    listEl.innerHTML = '<span class="hint">Could not load your keys.</span>';
  }
}

window.gdbsRotateApiKey = async function() {
  const btn = document.getElementById('btn-rotate-apikey');
  if (!confirm('Rotate your API key?\n\nThis issues a NEW key and immediately revokes your previous key(s). Any integration still using the old key will stop working until you update it.')) return;
  if (btn) { btn.disabled = true; btn.dataset._t = btn.innerHTML; btn.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Rotating...'; }
  try {
    const res = await api.advRotateApiKey('rotated ' + new Date().toISOString().slice(0, 10));
    if (res && res.key) {
      const box = document.getElementById('apikey-reveal');
      const val = document.getElementById('apikey-reveal-value');
      if (val) val.textContent = res.key;
      if (box) box.classList.remove('hidden');
      await loadApiKeysList();
    } else {
      alert(res?.error || 'Could not rotate the API key.');
    }
  } catch (_) {
    alert('Could not rotate the API key.');
  } finally {
    if (btn) { btn.disabled = false; btn.innerHTML = btn.dataset._t || '<i class="fas fa-rotate"></i> Rotate API key'; }
  }
};

window.gdbsCopyRevealedKey = function() {
  const v = document.getElementById('apikey-reveal-value')?.textContent;
  if (v) navigator.clipboard.writeText(v).then(() => alert('New API key copied. Store it securely - it will not be shown again.'));
};

// When authenticated, auto-connect and load schema
auth.onAuthenticated(async (user, license) => {
  // Server-push stream: open the single shared EventSource ONCE after auth
  // resolves, so the per-surface pollers can switch from tight timers to
  // fetch-on-event. Wrapped so a stream failure can NEVER abort the auth
  // cascade; if EventSource is unavailable, callers keep their slow safety
  // timers and nothing regresses.
  try { events.connect(); } catch (_) {}
  // Admin gating first — before any async work that could fail
  admin.showAdminIfAllowed(user);
  if (user?.role === 'admin') admin.loadUsers();
  // Share-link tool — admin-only "Share" button in the topbar (X/M365-style).
  shareUI.onAuth(user);
  // App Guide menu entry — revealed for the granted RBAC role (or admin)
  applyAppGuideAccess(user, license);
  // Compute-node toggle — available to authenticated users; auto-resumes if on
  applyComputeNode(user, license);
  // Anti-snoop bypass for admins
  try {
    const as = await import('./anti-snoop.js');
    as.setAdminBypass(user?.role === 'admin');
  } catch (_) {}
  // STD engine gating (materials/geophysics/topology/fluids/quantum) stays
  // static + free — runs for every authed user, unconditionally.
  materialsTabs.applyMaterialsGating();
  geophysicsTabs.applyGeoGating();
  window.gdbsApplyTopologyGating?.();
  fluidsTabs.applyFluidsGating();
  quantumTabs.applyQuantumGating();
  dsoModule.applyDsoGating();

  // PRO tier (plasma/molecular/cosmic): load the chunk + init + gate ONLY when
  // entitled. Wrapped so a failed PRO chunk can never abort the auth cascade;
  // an unentitled user sees the static-HTML paywall (gate divs default visible,
  // content divs default hidden), so no JS gating call is needed for them.
  if (_isProEntitled(user, license)) {
    try {
      const { plasmaTabs, molecularTabs, cosmicTabs } = await _loadProTier();
      plasmaTabs.applyLicenseGating();   // plasma-tabs handles all plasma panels
      molecularTabs.applyMolecularGating();
      cosmicTabs.applyCosmicGating();
    } catch (e) { console.error('[tier] PRO chunk load failed:', e); }
  }

  // Theory Lab is FREE for any active account — load + gate it on its own (does
  // NOT pull the heavy hpc-* engine graph).
  if (license?.isActive) {
    try {
      const { theoryLab } = await _loadTheory();
      theoryLab.applyTheoryGating();
    } catch (e) { console.error('[tier] Theory chunk load failed:', e); }
  }

  // HPC tier (hpc-lab + the hpc-* engine set, tukey, ns2d/heat/rayleigh/bssn/
  // ligo/lbm/io): load + init + gate ONLY when entitled. Unentitled users keep
  // the static HPC/tukey paywall; wrapped so a failed HPC chunk can never abort
  // the auth cascade.
  if (_isHpcEntitled(user, license)) {
    try {
      const { hpcLab, tukeyTabs } = await _loadHpcTier();
      tukeyTabs.applyTukeyGating();
      hpcLab.applyHpcGating();
    } catch (e) { console.error('[tier] HPC chunk load failed:', e); }
  }
  // Worklog seat add-on: reveal nav/view + co-brand to GDBSPro+Worklog when the
  // Reveal the Worklog gutter link (-> /worklog.html, its own GDBSPro+Worklog
  // page) when the seat is active. The real boundary is server-side; this is
  // just the launcher's visibility.
  // Admin-ness is the user ROLE (not a license tier), matching the server HasSeat
  // gate; an admin's license tier is usually 'pro'/etc., so checking tier hid the
  // link from admins.
  // Worklog access = admin OR the 'worklog' module OR a paid Pro/HPC tier
  // (mirrors the server HasSeat gate, which now includes Pro/HPC). "Locked if
  // not Pro or HPC."
  const _wlTier = (license?.tier || '').toLowerCase();
  const _wlEntitled = (user?.role === 'admin'
    || (license?.modules || []).includes('worklog')
    || ['pro', 'pro_monthly', 'premium', 'hpc', 'hpc_monthly', 'enterprise'].includes(_wlTier));
  const _wlNav = document.getElementById('nav-worklog');
  if (_wlNav) _wlNav.style.display = _wlEntitled ? '' : 'none';
  // Sandbox: reveal the nav + render the workspace when entitled (sandbox module
  // OR Pro/Enterprise/Research tier OR admin); otherwise the nav stays hidden and
  // the view shows an honest informational panel (custom pricing, no checkout).
  try { applySandboxAccess(user, license); } catch (_) {}
  // Gate client-portal add-on: reveal the gutter pill + unlock the product tab
  // when entitled. Gate is PAID ON EVERY TIER (unlike worklog it is NOT bundled
  // into Pro/HPC), so entitlement = admin OR the 'gate' module only - NEVER a
  // tier. Mirrors GateEntitlement.HasGate (IncludedTiers empty). The real
  // boundary is server-side on GateController; this just controls launcher
  // visibility, exactly like the worklog block above.
  const _gateEntitled = (user?.role === 'admin'
    || (license?.modules || []).includes('gate'));
  (function applyGateGating() {
    const navGate = document.getElementById('nav-gate');
    if (navGate) navGate.style.display = _gateEntitled ? '' : 'none';
    // The Gate product tab is always visible but locked when not entitled;
    // clicking it then routes to pricing (the $9.99/user/mo add-on).
    const gateTab = document.getElementById('tab-gate');
    if (gateTab) {
      gateTab.classList.toggle('locked', !_gateEntitled);
      gateTab.onclick = (e) => { if (!_gateEntitled) { e.preventDefault(); if (window.showPricing) window.showPricing(); } };
    }
  })();
  // Top product tabs: GDBSPro (active) | Worklog | All Tools. Worklog stays
  // VISIBLE but LOCKED (dim + lock, click -> pricing) when not Pro/HPC; All Tools
  // opens the Browse megamenu (replaces the removed gutter Browse launcher).
  (function applyProductTabs() {
    const wlTab = document.getElementById('tab-worklog');
    if (wlTab) {
      wlTab.classList.toggle('locked', !_wlEntitled);
      wlTab.onclick = (e) => { if (!_wlEntitled) { e.preventDefault(); if (window.showPricing) window.showPricing(); } };
    }
    const atTab = document.getElementById('tab-alltools');
    if (atTab && !atTab._wired) {
      atTab._wired = true;
      atTab.addEventListener('click', (e) => { e.preventDefault(); if (window.gdbsMegamenu) window.gdbsMegamenu.open(); });
    }
  })();
  // HPC Sim — locked to admin only until verified
  (function applyHpcSimGating() {
    const isAdmin = user?.role === 'admin';
    const hpcSimNav = document.querySelector('[data-view="hpcsim"]');
    const hpcSimView = document.getElementById('view-hpcsim');
    if (!isAdmin) {
      if (hpcSimNav) hpcSimNav.style.display = 'none';
      if (hpcSimView) hpcSimView.innerHTML = '<div style="padding:60px;text-align:center;"><i class="fas fa-lock" style="font-size:32px;color:#374151;margin-bottom:12px;display:block;"></i><h3 style="color:#64748b;">HPC Sim — Coming Soon</h3><p style="color:#475569;font-size:13px;">This module is currently in development.</p></div>';
    }
  })();
  applyApiDocsGating(user, license);
  // Auto-connected to built-in GDBS instance — no manual connection needed
  queryEditor.setConnection('local');
  dbBrowser.setConnection('local');
  importWizard.setConnection('local');
  dbBrowser.loadSchema();
  // STD engines' WASM warms unconditionally (free for all). PRO engines' WASM
  // (plasma/molecular/cosmic) warms inside _loadProTier, gated by entitlement.
  // Warm AFTER first paint, not on the boot critical path: the engine binary is
  // ~3.3MB and ~93% of sessions never run a wasm engine, so blocking boot on it
  // hurt time-to-interactive for everyone. Idle-scheduled (timeout bounds the wait
  // if the tab stays busy); setTimeout fallback for engines without requestIdleCallback.
  const _warmStdEngines = () => {
    materialsTabs.initWasm();
    geophysicsTabs.initWasm();
    fluidsTabs.initWasm();
    quantumTabs.initWasm();
  };
  (window.requestIdleCallback
    ? window.requestIdleCallback(_warmStdEngines, { timeout: 3000 })
    : setTimeout(_warmStdEngines, 800));
});

// Public newsletter subscribe (landing footer). Double opt-in: the server emails a
// confirm link and always returns the same generic response, so the form never
// reveals whether an address is already subscribed.
document.getElementById('landing-subscribe')?.addEventListener('submit', async (e) => {
  e.preventDefault();
  const input = document.getElementById('landing-sub-email');
  const status = document.getElementById('landing-sub-status');
  const email = (input?.value || '').trim();
  if (!email) return;
  status.textContent = 'Submitting...';
  status.style.color = '#64748b';
  try {
    const res = await fetch('/api/email/subscribe', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }),
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) { status.textContent = data.message || 'Check your email to confirm.'; status.style.color = '#16a34a'; e.target.reset(); }
    else { status.textContent = data.error || 'Could not subscribe. Please try again.'; status.style.color = '#dc2626'; }
  } catch { status.textContent = 'Network error. Please try again.'; status.style.color = '#dc2626'; }
});

// Research access request form. Research grants are free in exchange for a citation
// or testimonial (limited); there is no academic/student discount pricing.
document.getElementById('f-beta-request')?.addEventListener('submit', async (e) => {
  e.preventDefault();
  const btn = document.getElementById('btn-beta-submit');
  const status = document.getElementById('beta-request-status');
  const name = document.getElementById('beta-name')?.value?.trim();
  const email = document.getElementById('beta-email')?.value?.trim();
  const institution = document.getElementById('beta-institution')?.value?.trim();
  const department = document.getElementById('beta-department')?.value?.trim() || '';
  const useCase = document.getElementById('beta-usecase')?.value?.trim();
  const agreed = document.getElementById('beta-cite-agree')?.checked;

  if (!name || !email || !institution || !useCase || !agreed) {
    status.textContent = 'All required fields must be completed.';
    status.style.color = 'var(--error)';
    return;
  }

  btn.disabled = true;
  btn.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Submitting...';
  status.textContent = '';

  try {
    const formData = new FormData();
    formData.append('requestType', 'research');
    formData.append('name', name);
    formData.append('email', email);
    formData.append('institution', institution);
    formData.append('department', department);
    formData.append('useCase', useCase);

    const res = await fetch('/api/auth/beta-request', {
      method: 'POST',
      body: formData
    });
    const data = await res.json();
    if (res.ok) {
      status.textContent = 'Research access request submitted. Our team will review and respond via email.';
      status.style.color = 'var(--success)';
      e.target.reset();
    } else {
      status.textContent = data?.error || 'Failed to submit. Please try again.';
      status.style.color = 'var(--error)';
    }
  } catch {
    status.textContent = 'Network error. Please try again later.';
    status.style.color = 'var(--error)';
  } finally {
    btn.disabled = false;
    btn.innerHTML = '<i class="fas fa-paper-plane"></i> Submit Request';
  }
});
